<template>
  <div>
    <div class="mb-4 d-flex align-items-center justify-content-between">
      <h4 class="m-0">Report Tags</h4>

      <slot name="title-extra" />
    </div>

    <b-form-group class="mb-4">
      <b-input-group class="input-group-alternative input-group-merge">
        <div class="input-group-prepend">
          <span class="input-group-text"><i class="fas fa-search"></i></span>
        </div>
        <b-form-input
          v-model="searchKeyword"
          placeholder="Search"
          type="text"
        ></b-form-input>
      </b-input-group>
    </b-form-group>

    <div class="checkbox-list">
      <CollapseTransition tag="div" group>
        <template v-if="optionsList.length">
          <div
            v-for="(optionItem, optionIndex) in optionsList"
            :key="optionIndex"
            class="position-relative"
          >
            <SlideXLeftTransition tag="div">
              <div
                v-if="optionItem.value == toDeleteID"
                class="confirmation-overlay w-100 h-100 bg-light py-1 px-2 text-sm d-flex align-items-center justify-content-between position-absolute top-0 left-0 z-5"
              >
                Are you sure you want to delete this?

                <span>
                  <b-link
                    @click.prevent.stop="() => handleDelete(toDeleteID)"
                    class="confirmation-overlay__action mx-1 rounded-circle text-white bg-primary d-inline-flex align-items-center justify-content-center"
                  >
                    <b-icon icon="check"></b-icon>
                  </b-link>
                  <b-link
                    @click.prevent.stop="() => handlePreDelete(null)"
                    class="confirmation-overlay__action mx-1 rounded-circle text-white bg-danger d-inline-flex align-items-center justify-content-center"
                  >
                    <b-icon icon="x"></b-icon>
                  </b-link>
                </span>
              </div>
            </SlideXLeftTransition>

            <input
              v-model="innerValue"
              :value="optionItem.value"
              :id="`tag-${optionItem.value}`"
              type="checkbox"
              class="d-none"
            />
            <label
              :for="`tag-${optionItem.value}`"
              class="py-1 pr-3 mb-0 d-flex align-items-center justify-content-between position-relative"
            >
              <span>{{ optionItem.text }}</span>
              <span>
                <b-link
                  @click.stop="() => handlePreUpdate(optionItem.value, optionItem.text)"
                  class="text-primary"
                >
                  <b-icon icon="pencil" font-scale="0.975"></b-icon>
                </b-link>
                <b-link
                  @click.stop="() => handlePreDelete(optionItem.value)"
                  class="ml-2 text-danger"
                >
                  <b-icon icon="trash" font-scale="0.975"></b-icon>
                </b-link>
              </span>
            </label>

            <slot name="option-extras" :option="optionItem" />
          </div>
        </template>
        <div v-else key="empty">
          <small>No tags found.</small>
        </div>
      </CollapseTransition>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, ref, onMounted } from "@vue/composition-api";
import { createVuexHelpers } from "vue2-helpers";
import { CollapseTransition, SlideXLeftTransition } from "vue2-transitions";
import { orderBy } from "lodash-es";
import Fuse from "fuse.js";

import { ReportsService } from "@services";

export default defineComponent({
  name: "TagsList",
  components: {
    CollapseTransition,
    SlideXLeftTransition,
  },
  emits: ["input", "form:edit"],
  props: {
    value: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const { useGetters, useActions } = createVuexHelpers();

    const { tags, reports } = useGetters(["tags", "reports"]);
    const { fetchTags, readTag, deleteTag } = useActions([
      "fetchTags",
      "readTag",
      "deleteTag",
    ]);

    const searchKeyword = ref("");
    const toDeleteID = ref(null);

    const innerValue = computed({
      get: () => props.value,
      set: (newValue) => {
        emit("input", newValue);
      },
    });

    const optionsList = computed(() => {
      const tagsList = tags.value.length
        ? orderBy(
            tags.value.map((item) => ({
              value: item.id,
              text: item.name,
            })),
            ["text"],
            ["asc"]
          )
        : [];

      if (searchKeyword.value) {
        const fuse = new Fuse(tagsList, {
          keys: ["text"],
          useExtendedSearch: true,
        });

        return fuse.search(`'${searchKeyword.value}`).map((item) => item.item);
      }

      return tagsList;
    });

    const handlePreUpdate = async (id, tag) => {
      await readTag(id);

      emit("form:edit", true);
    };

    const handlePreDelete = (id) => {
      toDeleteID.value = id;
    };

    const handleDelete = async (id) => {
      await deleteTag(id);

      if (reports.value.results.length) {
        const reportsList = reports.value.results.filter(
          (item) => !!item.tags.find((tag) => tag.id == id)
        );

        for await (const reportItem of reportsList) {
          await ReportsService.assignTags(reportItem.id, [
            {
              type: "remove",
              tag: id,
            },
          ]);

          const tagIndex = reportItem.tags.findIndex((tag) => tag.id == id);

          reportItem.tags.splice(tagIndex, 1);
        }
      }
    };

    onMounted(fetchTags);

    return {
      tags,
      searchKeyword,
      innerValue,
      optionsList,
      toDeleteID,
      handlePreUpdate,
      handlePreDelete,
      handleDelete,
    };
  },
});
</script>

<style lang="scss" scoped>
.checkbox-list {
  input {
    + label {
      &::before,
      &::after {
        content: "";
        width: 15px;
        height: 15px;
        border-width: 1px;
        border-color: #ced4da;
        border-style: solid;
        border-radius: 50%;
        position: absolute;
        top: 8px;
        left: 3px;
      }

      &::after {
        width: 5px;
        height: 8px;
        border-width: 0 2px 2px 0;
        top: 11px;
        left: 8px;
        display: none;
        -webkit-transform: rotate(45deg);
        transform: rotate(45deg);
      }

      padding-left: 30px;
      cursor: pointer;
    }

    &:checked + label {
      &::before {
        background-color: #1c4a50;
        border-color: #1c4a50;
        box-shadow: #c4dbdf 0 0 0 3px;
      }

      &::after {
        display: block;
      }
    }
  }
}

.confirmation-overlay {
  z-index: 9;

  &__action {
    width: 18px;
    height: 18px;
  }
}
</style>
